/**
 * Cadastro de pessoas — e, junto, o controle de acesso ao sistema.
 *
 * O QUE ESTA TELA REALMENTE FAZ: estar autenticado no Google prova quem você é;
 * quem decide o que você vê é esta tabela. Uma conta Google válida sem pessoa
 * cadastrada não entra, e pessoa cadastrada sem nenhum módulo marcado também
 * não. O controle é explícito por decisão — nada é liberado por padrão.
 *
 * Por isso os módulos aparecem como caixas de seleção, e não escondidos num
 * campo de texto: quem libera acesso precisa VER o que está liberando.
 *
 * Mestre de obra é o caso que explica por que e-mail é opcional: ele é cadastro
 * de referência (o nome sugerido no campo "quem recebeu" da entrega), não
 * alguém que entra no sistema.
 */

import { api, ErroApi } from '../../api.js';
import { escapar } from '../../comum/formato.js';
import { avisar, avisarBloqueante } from '../../comum/aviso.js';
import { MODULOS } from '../../modulos.js';

interface Pessoa {
  id: number;
  nome: string;
  funcao: string | null;
  departamento: string | null;
  telefone: string | null;
  email: string | null;
  pode_autorizar: boolean;
  status: 'ativo' | 'pendente' | 'bloqueado';
  modulos: string[];
  /** Usuário master: marca própria (migração 038), separada do módulo Administração. */
  master: boolean;
}

const SITUACOES: { valor: Pessoa['status']; rotulo: string; explica: string }[] = [
  { valor: 'ativo', rotulo: 'Ativo', explica: 'Entra normalmente.' },
  { valor: 'pendente', rotulo: 'Pendente', explica: 'Cadastrado, mas ainda sem liberação para entrar.' },
  { valor: 'bloqueado', rotulo: 'Bloqueado', explica: 'Não entra, e o histórico dele fica preservado.' },
];

export function montarCadastroPessoas(raiz: HTMLElement): void {
  raiz.innerHTML = `
    <div class="barra-busca">
      <input id="busca" placeholder="Procure por nome, função ou e-mail…" autocomplete="off">
      <button class="btn" id="nova">+ Nova pessoa</button>
    </div>
    <div id="lista"><p class="carregando">Carregando…</p></div>`;

  const lista = raiz.querySelector<HTMLElement>('#lista')!;
  const campoBusca = raiz.querySelector<HTMLInputElement>('#busca')!;
  let todas: Pessoa[] = [];
  /** Quem está usando a tela é master? Só master liga ou desliga a marca de alguém. */
  let souMaster = false;

  campoBusca.addEventListener('input', () => desenhar());
  raiz.querySelector('#nova')!.addEventListener('click', () => desenharFicha(null));

  async function carregar() {
    try {
      const [r, eu] = await Promise.all([
        api.get<{ pessoas: Pessoa[] }>('/api/pessoas'),
        api.get<{ master?: boolean }>('/api/eu'),
      ]);
      todas = r.pessoas;
      souMaster = eu.master === true;
      desenhar();
    } catch (erro) {
      lista.innerHTML = `<p class="aviso">${escapar(
        erro instanceof Error ? erro.message : 'Não consegui carregar.',
      )}</p>`;
    }
  }

  function desenhar() {
    const termo = campoBusca.value.trim().toLowerCase();
    const pessoas = termo
      ? todas.filter((p) =>
          [p.nome, p.funcao, p.email, p.departamento]
            .filter(Boolean)
            .some((v) => v!.toLowerCase().includes(termo)),
        )
      : todas;

    if (pessoas.length === 0) {
      lista.innerHTML = `<div class="vazio"><p>Ninguém${termo ? ' para essa busca' : ''}.</p></div>`;
      return;
    }

    const semAcesso = todas.filter((p) => p.modulos.length === 0).length;
    /**
     * Módulo liberado + sem e-mail = acesso que não funciona.
     *
     * Não é erro de digitação, é contradição: o login do Google identifica a
     * pessoa PELO e-mail, então quem não tem um nunca entra — por mais módulos
     * que estejam marcados. Sem este aviso, a pessoa que liberou o acesso acha
     * que liberou, e só descobre quando alguém reclama que não consegue entrar.
     */
    const acessoQueNaoFunciona = todas.filter((p) => p.modulos.length > 0 && !p.email);

    lista.innerHTML = `
      ${
        acessoQueNaoFunciona.length
          ? `<p class="aviso">
               ${acessoQueNaoFunciona.map((p) => escapar(p.nome)).join(', ')}
               ${acessoQueNaoFunciona.length === 1 ? 'tem módulo liberado' : 'têm módulos liberados'}
               mas <b>não tem e-mail</b> — o login é pelo e-mail, então
               ${acessoQueNaoFunciona.length === 1 ? 'ela não consegue' : 'elas não conseguem'} entrar.
             </p>`
          : ''
      }
      <p class="dica">
        ${pessoas.length} pessoa(s).
        ${
          semAcesso
            ? `<b>${semAcesso}</b> sem nenhum módulo — são cadastro de referência, não entram no sistema.`
            : ''
        }
      </p>
      <div class="rolagem">
        <table class="tabela">
          <thead><tr>
            <th>Nome</th><th>Função</th><th>E-mail</th><th>Acesso</th><th>Situação</th><th></th>
          </tr></thead>
          <tbody>${pessoas
            .map(
              (p) => `<tr>
                <td>${escapar(p.nome)}${p.master ? ' <span class="etiqueta">👑 master</span>' : ''}${
                  p.pode_autorizar ? '<span class="sub">pode autorizar retirada</span>' : ''
                }</td>
                <td>${escapar(p.funcao ?? '—')}</td>
                <td>${p.email ? escapar(p.email) : '<span class="falta">—</span>'}</td>
                <td>${
                  p.modulos.length
                    ? p.modulos
                        .map((m) => `<span class="etiqueta">${escapar(nomeDoModulo(m))}</span>`)
                        .join(' ')
                    : '<span class="falta">sem acesso</span>'
                }</td>
                <td>${escapar(SITUACOES.find((s) => s.valor === p.status)?.rotulo ?? p.status)}</td>
                <td><button class="btn btn-claro" data-abrir="${p.id}">Abrir</button></td>
              </tr>`,
            )
            .join('')}</tbody>
        </table>
      </div>`;

    lista.querySelectorAll<HTMLElement>('[data-abrir]').forEach((b) => {
      b.addEventListener('click', () => {
        desenharFicha(todas.find((p) => p.id === Number(b.dataset['abrir'])) ?? null);
      });
    });
  }

  function desenharFicha(pessoa: Pessoa | null) {
    const novo = !pessoa;

    lista.innerHTML = `
      <button class="voltar" id="voltar">← Todas as pessoas</button>
      <form class="ficha" id="ficha" novalidate>
        <header class="ficha-topo">
          <div>
            <span class="rotulo">${novo ? 'Nova pessoa' : 'Pessoa'}</span>
            <h2>${escapar(pessoa?.nome ?? 'Cadastrar')}</h2>
          </div>
        </header>

        <div class="form-grade">
          <label class="rotulado largo">
            <span>Nome</span>
            <input name="nome" value="${escapar(pessoa?.nome ?? '')}" maxlength="200" required>
          </label>
          <label class="rotulado">
            <span>Função</span>
            <input name="funcao" value="${escapar(pessoa?.funcao ?? '')}" maxlength="120">
          </label>
          <label class="rotulado">
            <span>Departamento</span>
            <input name="departamento" value="${escapar(pessoa?.departamento ?? '')}" maxlength="120">
          </label>
          <label class="rotulado">
            <span>Telefone</span>
            <input name="telefone" value="${escapar(pessoa?.telefone ?? '')}" maxlength="40">
          </label>
          <label class="rotulado largo">
            <span>E-mail</span>
            <input name="email" type="email" value="${escapar(pessoa?.email ?? '')}">
            <small class="dica">É por ele que o login do Google reconhece a pessoa. Vazio = não entra no sistema.</small>
          </label>
          <label class="rotulado estreito caixa">
            <input type="checkbox" name="podeAutorizar" ${pessoa?.pode_autorizar ? 'checked' : ''}>
            <span>Pode autorizar retirada</span>
          </label>
        </div>

        <h3>Situação</h3>
        <div class="opcoes">
          ${SITUACOES.map(
            (s) => `
            <label class="opcao">
              <input type="radio" name="status" value="${s.valor}"
                     ${(pessoa?.status ?? 'pendente') === s.valor ? 'checked' : ''}>
              <span><b>${s.rotulo}</b><small>${escapar(s.explica)}</small></span>
            </label>`,
          ).join('')}
        </div>

        <h3>Módulos liberados</h3>
        <p class="dica">
          Nada é liberado por padrão. Sem nenhum marcado, a pessoa não entra —
          o que é o certo para quem é só cadastro de referência, como mestre de obra.
        </p>
        <div class="opcoes">
          ${MODULOS.map(
            (m) => `
            <label class="opcao">
              <input type="checkbox" name="modulos" value="${m.chave}"
                     ${pessoa?.modulos.includes(m.chave) ? 'checked' : ''}>
              <span><b>${m.emoji} ${escapar(m.nome)}</b><small>${escapar(m.descricao)}</small></span>
            </label>`,
          ).join('')}
        </div>

        <h3>Usuário master</h3>
        <p class="dica">
          Master é diferente de ter o módulo Administração: a pessoa pode ter a Administração e não ser master.
          Só um master torna outra pessoa master, e só um master altera os próprios módulos.
          O sistema nunca fica sem nenhum master ativo.
        </p>
        <div class="opcoes">
          <label class="opcao">
            <input type="checkbox" name="master" ${pessoa?.master ? 'checked' : ''} ${souMaster ? '' : 'disabled'}>
            <span><b>👑 Esta pessoa é master</b><small>${
              souMaster ? 'Marque para dar, desmarque para tirar.' : 'Só um usuário master muda esta opção.'
            }</small></span>
          </label>
        </div>

        <h3>Observações</h3>
        <label class="rotulado"><textarea name="obs" rows="3" maxlength="2000"></textarea></label>

        <div class="conf-acoes">
          <button type="submit" class="btn" id="salvar">${novo ? 'Cadastrar' : 'Salvar'}</button>
          ${
            pessoa?.email
              ? `<button type="button" class="btn btn-claro" id="reenviar-acesso">✉️ Reenviar e-mail de acesso</button>`
              : ''
          }
        </div>
        <p class="aviso" id="erro" hidden></p>
      </form>`;

    lista.querySelector('#voltar')!.addEventListener('click', () => void carregar());
    const form = lista.querySelector<HTMLFormElement>('#ficha')!;
    form.addEventListener('submit', (e) => {
      e.preventDefault();
      void salvar(form, pessoa);
    });
    // Para quem perdeu o e-mail de boas-vindas — reenvia com os módulos atuais.
    form.querySelector('#reenviar-acesso')?.addEventListener('click', () => {
      const b = form.querySelector<HTMLButtonElement>('#reenviar-acesso')!;
      b.disabled = true;
      b.textContent = '⏳ Enviando…';
      void api
        .post<{ para: string }>(`/api/pessoas/${pessoa!.id}/email-acesso`, {})
        .then((r) => avisar(`E-mail de acesso enviado para ${r.para}.`))
        .catch((e) => avisarBloqueante(e instanceof ErroApi ? e.message : 'Não consegui enviar.'))
        .finally(() => {
          b.disabled = false;
          b.textContent = '✉️ Reenviar e-mail de acesso';
        });
    });
  }

  async function salvar(form: HTMLFormElement, pessoa: Pessoa | null) {
    const erro = form.querySelector<HTMLElement>('#erro')!;
    const d = new FormData(form);
    const texto = (n: string) => String(d.get(n) ?? '').trim();

    if (!texto('nome')) {
      erro.textContent = 'O nome é obrigatório.';
      erro.hidden = false;
      return;
    }

    const corpo = {
      nome: texto('nome'),
      funcao: texto('funcao') || null,
      departamento: texto('departamento') || null,
      telefone: texto('telefone') || null,
      email: texto('email') || null,
      podeAutorizar: d.get('podeAutorizar') === 'on',
      status: texto('status') || 'pendente',
      modulos: d.getAll('modulos').map(String),
      // Caixa desabilitada não vai no formulário: quem não é master não manda o campo, e o servidor mantém o que está.
      ...(souMaster ? { master: d.get('master') === 'on' } : {}),
      obs: texto('obs') || null,
    };

    const botao = form.querySelector<HTMLButtonElement>('#salvar')!;
    botao.disabled = true;
    botao.textContent = 'Salvando…';
    try {
      const r = pessoa
        ? await api.put<{ emailEnviado?: boolean }>(`/api/pessoas/${pessoa.id}`, corpo)
        : await api.post<{ emailEnviado?: boolean }>('/api/pessoas', corpo);
      // A liberação manda o e-mail de boas-vindas — a tela conta o que houve.
      if (r.emailEnviado === true) {
        avisar(`${corpo.nome} salvo(a) — e-mail de acesso enviado para ${corpo.email}.`);
      } else if (r.emailEnviado === false) {
        avisar(`${corpo.nome} salvo(a), mas o e-mail de acesso NÃO saiu — use o "Reenviar".`, 'atencao');
      } else {
        avisar(pessoa ? `${corpo.nome} salvo(a)` : `${corpo.nome} cadastrado(a)`);
      }
      await carregar();
    } catch (e) {
      botao.disabled = false;
      botao.textContent = pessoa ? 'Salvar' : 'Cadastrar';
      erro.textContent =
        e instanceof ErroApi
          ? e.message + (e.detalhes?.length ? ` (${e.detalhes.join('; ')})` : '')
          : 'Não consegui salvar.';
      erro.hidden = false;
    }
  }

  function nomeDoModulo(chave: string): string {
    return MODULOS.find((m) => m.chave === chave)?.nome ?? chave;
  }

  void carregar();
}
